import React, { useState, ReactNode, useMemo } from "react";
import { View, StyleSheet, Dimensions, ViewStyle } from "react-native";
import { colors, layout, shadows } from "@/constants";
import Interactive from "./Interactive";
import ScrollView from "./ScrollView";
import Icon from "./Icon";

/**
 * Props for the Accordion component
 */
interface AccordionProps {
  /** Custom header content (icon, title, etc.) */
  headerChildren: ReactNode;
  /** Content to show when expanded */
  contentChildren: ReactNode;
  /** Whether the accordion starts expanded */
  defaultExpanded?: boolean;
  /** Optional style overrides for the container */
  style?: ViewStyle;
  /** Accessibility label for the accordion */
  accessibilityLabel?: string;
  /** Whether the accordion is disabled (cannot be expanded) */
  disabled?: boolean;
}

/**
 * Generic collapsible accordion component with header and expandable content.
 * Provides consistent styling similar to ErrorDisplay for UI consistency.
 *
 * @param props - The component props
 * @returns A collapsible accordion container
 *
 * @example
 * ```tsx
 * <Accordion
 *   headerChildren={
 *     <>
 *       <Icon name="info" size="medium" />
 *       <Text>Session Summary</Text>
 *     </>
 *   }
 *   contentChildren={<Text>Your content here</Text>}
 *   defaultExpanded={false}
 * />
 * ```
 */
export default function Accordion({
  headerChildren,
  contentChildren,
  defaultExpanded = false,
  style,
  accessibilityLabel,
  disabled = false
}: AccordionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Calculate max height as 70% of screen height to cover most of the screen
  const maxHeight = useMemo(() => {
    const screenHeight = Dimensions.get("window").height;
    return screenHeight * 0.7;
  }, []);

  const toggleExpanded = () => {
    if (!disabled) {
      setIsExpanded((prev) => !prev);
    }
  };

  return (
    <View style={[styles.container, style]}>
      <Interactive
        style={styles.header}
        onPress={toggleExpanded}
        disabled={disabled}
        accessibilityState={{ expanded: isExpanded, disabled }}
        accessibilityLabel={
          accessibilityLabel ||
          `Accordion, ${isExpanded ? "expanded" : "collapsed"}${disabled ? ", disabled" : ""}`
        }
      >
        <View style={styles.headerLeft}>{headerChildren}</View>
        {!disabled && (
          <Icon
            name={isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
            size="medium"
            color="primary"
          />
        )}
      </Interactive>

      {isExpanded && (
        <ScrollView
          style={{ maxHeight }}
          contentContainerStyle={styles.content}
        >
          {contentChildren}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.aiContainer,
    borderRadius: layout.borderRadius.large,
    marginHorizontal: layout.space.lg,
    marginVertical: layout.space.sm,
    // Clip the header's hover/press overlay to the container's rounded corners.
    overflow: "hidden",
    ...shadows.raised
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: layout.space.md
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.sm,
    flex: 1
  },
  content: {
    gap: layout.space.md,
    paddingHorizontal: layout.space.md,
    paddingTop: layout.space.xs,
    paddingBottom: layout.space.md
  }
});
